import { useStore } from 'hooks/useStore';
import { AddIteractionCheckBoxRequestPayload } from 'Interfaces/BDPortalInterfaces/AddNewIterationInterfaces';
import { TreeNode } from 'primereact/treenode';
import { TreeCheckboxSelectionKeys } from 'primereact/tree';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { bdportalScreenServices } from 'services/bdportalScreenServices';
import {
  AddIterationNodeServices,
  createEmptyRequestPayloadForAddIteration,
} from 'utils/bdTrackerUtilities/addIterationModalUtilities';
import { deepCopy } from 'utils/deepCopy';

const useAddIterationLogic = (
  _visibility: boolean,
  _handleClose: (newIterationCreated?: boolean) => void,
  opportunityID: number,
  opportunityIterationID: number,
  board: string
) => {
  const {
    UserStore: { userModulesRoles },
  } = useStore();
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<TreeCheckboxSelectionKeys | null>(null);
  const [isIterationLoading, setIsIterationLoading] = useState(false);

  const [boardType, setBoardType] = useState('');

  // NC Details (tree node index 3) is only applicable to NC iterations.
  // It must be fully locked — neither auto-selected by "Select All" nor
  // toggleable in the tree — whenever the iteration/board is MAS.
  const isNCDetailsLocked = boardType === 'MAS' || board === 'MAS';

  const checkFieldInPayload = (payload: Record<string, unknown>, field: string) => {
    Object.keys(Object(payload)).forEach(key => {
      if (key === field) {
        payload[key] = true;
      }
    });
  };

  const handleAddButtonClick = async () => {
    try {
      if (!opportunityIterationID || !opportunityID || !board || !boardType) return;
      setIsIterationLoading(true);
      const payload: AddIteractionCheckBoxRequestPayload =
        createEmptyRequestPayloadForAddIteration();

      // create payload from seledkeys
      Object.keys(Object(selectedKeys)).forEach(key => {
        const indexes = key.split('-');
        // check if length is 2 or 1 to use nested values
        if (indexes.length === 2) {
          checkFieldInPayload(payload, nodes[Number(indexes[0])].children?.[Number(indexes[1])]?.data);
        } else {
          checkFieldInPayload(payload, nodes[Number(indexes[0])].data);
        }
      });

      payload.OpportunityIterationsID = opportunityIterationID;
      payload.OpportunityID = opportunityID;
      payload.old_board_type = board;
      payload.new_board_type = boardType;
      payload.SourceID = true;
      const res = await bdportalScreenServices.createNewIteration(payload);

      console.log({ res });
      toast.success(res.Message);
      window.location.href = `/bdportal/optracker/board?opportunityIdParam=${res?.opportunityId}&opportunityIterationIdParam=${res?.iterationId}`;

      //   // New iteration is created successfully so pass true to handle close function
      //   handleClose(true);
    } catch (_err) {
      toast.error('Could not add new iteration, please try again later');
    } finally {
      setIsIterationLoading(false);
    }
  };

  useEffect(() => {
    AddIterationNodeServices.getTreeNodes().then(data => setNodes(data));
  }, []);

  useEffect(() => {
    if (board) {
      setBoardType(board);
    }
  }, [board]);

  const selectAllOptions = () => {
    const tempSelectedKeys: TreeCheckboxSelectionKeys = {};
    for (let i = 0; i < nodes.length; i++) {
      // Skip NC Details (index 3) entirely when it is locked (MAS).
      if (i === 3 && isNCDetailsLocked) {
        continue;
      }
      tempSelectedKeys[i.toString()] = {
        checked: true,
        partialChecked: false,
      };
      if ('children' in nodes[i] && nodes[i].children) {
        nodes[i].children?.forEach((_child: TreeNode, j: number) => {
          tempSelectedKeys[`${i}-${j}`] = {
            checked: true,
            partialChecked: false,
          };
        });
      }
    }
    setSelectedKeys(tempSelectedKeys);
  };

  const unselectAllOptions = () => {
    setSelectedKeys({});
  };

  // Single choke point for all tree selection changes. When NC Details is
  // locked (MAS), strip any NC Details keys ('3', '3-*') so it can never be
  // selected via the tree — not by clicking, not by checkbox propagation.
  const handleSelectionChange = (value: TreeCheckboxSelectionKeys | null) => {
    if (isNCDetailsLocked && value) {
      const filtered: TreeCheckboxSelectionKeys = {};
      Object.keys(value).forEach(key => {
        if (!key.startsWith('3')) {
          filtered[key] = value[key];
        }
      });
      setSelectedKeys(filtered);
      return;
    }
    setSelectedKeys(value);
  };

  const checkIfAllSelected = () => {
    if (!selectedKeys) return false;
    let totalValidNodes = 0;
    for (let i = 0; i < nodes.length; i++) {
      // NC Details (index 3) is excluded from the count when it is locked (MAS).
      if (i === 3 && isNCDetailsLocked) {
        continue;
      }
      totalValidNodes++;
      if ('children' in nodes[i] && nodes[i].children) {
        totalValidNodes += nodes[i].children?.length ?? 0;
      }
    }
    if (selectedKeys && totalValidNodes === Object.keys(selectedKeys).length) return true;
    return false;
  };

  const unselectNCDetails = useCallback(() => {
    const tempSelectedKeys = deepCopy(selectedKeys);
    const tempNCDetailsRemovedSelectedKeys: TreeCheckboxSelectionKeys = {};
    Object.keys(Object(tempSelectedKeys)).forEach(key => {
      if (!key.startsWith('3') && tempSelectedKeys) {
        tempNCDetailsRemovedSelectedKeys[key] = tempSelectedKeys[key];
      }
    });
    setSelectedKeys(tempNCDetailsRemovedSelectedKeys);
  }, [selectedKeys]);

  useEffect(() => {
    if (boardType && nodes && nodes.length > 3) {
      const tempNodeDetails = deepCopy(nodes);
      if (boardType === 'MAS' || board === 'MAS') {
        tempNodeDetails[3].className = 'disabled-tree-table-checkbox';
        unselectNCDetails();
      } else {
        tempNodeDetails[3].className = '';
      }
      setNodes(tempNodeDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardType]);

  return {
    userModulesRoles,
    nodes,
    selectedKeys,
    setBoardType,
    setSelectedKeys,
    isIterationLoading,
    boardType,
    checkFieldInPayload,
    handleAddButtonClick,
    unselectNCDetails,
    selectAllOptions,
    unselectAllOptions,
    checkIfAllSelected,
    handleSelectionChange,
    isNCDetailsLocked,
  };
};

export default useAddIterationLogic;
